import React, { useCallback, useEffect, useState, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteProps, Switch } from 'react-router-dom';
import { FeatureTypes } from '@api/services/feature/feature.apiService.interfaces';
import { EducationPortalAsync } from '@components-member/EducationPortal';
import { PspOperationDetails } from '@components-member/Sales/PspOperations';
import { TradingAccountDetails } from '@components-member/Sales/TradingAccounts';
import { TradingAccountsRouters } from '@components-member/TradingAccounts';
import {
  BonusesAsync,
  InvestorRouterAsync,
  LegalDocumentsAsync,
  LegalTradingPlatformAsync,
  LegalTradingToolsAsync,
  LoyaltyProgramRouterAsync,
  MamCreateLPOAViaLinkAsync,
  MasterProfileAsync,
  MastersDetailsAsync,
  MastersRatingAsync,
  MyStatisticsAsync,
  NotificationAsync,
  OperationHistoryAsync,
  PartnershipAsync,
  ProfileAsync,
  PspAsync,
  ReferralLinkDetailsAsync,
  SalesAsync,
  SettingsAsync,
  TicketAsync,
  TradingContestRouterAsync,
  UserDetailsAsync,
  UserFAQAsync,
  UserTradingAccountsLayoutAsync,
} from '@components-member/asyncUserComponents';
import { UserJurisdictionPopupModalWithRouter } from '@components-member/modals/UserJurisdicionAcceptModal';
import { EnumUserRoute } from '@components-member/userRoutes/enums';
import { Page404 } from '@components-shared/404/404';
import { GuardCountrySupport } from '@components-shared/Guard/GuardCountrySupport';
import RouteWithPageTitle from '@components-shared/RouteWithPageTitle';
import { CONSTANTS } from '@components-shared/constants';
import HubSpotLiveChat from '@components-shared/liveChat/HubSpot';
import Preloader from '@components-shared/preloader';
import useDisableNumberInputScroll from '@components-shared/useDisableNumberInputScroll';
import { getAntdThemeConfig, userGlobalStyles } from '@styles-global/antdThemeConfig';
import { isOnboardingRestricted, ROUTE_CATEGORY } from '@utils/isOnboardingRestricted';
import { ConfigProvider } from 'antd';
//@ts-ignore
import TelemetryProvider from 'applicationInsights/TelemetryProvider';
import cn from 'classnames';
import { registrationSuccessfulMaPageEvent } from 'components/home/registration/analyticsEvents/registrationEvents';
import { UserWelcomeModal } from 'components/user/modals/userWelcome/userWelcome';
import { UserWithStatusNeedToUpdateModal } from 'components/user/modals/userWithStatusNeedToUpdate';
import { connectToContext } from 'contextStore';
import { CLIENT_SOURCE_TYPE } from 'global.enums';
import { DocumentDirection, LanguageType } from 'global.interfaces';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';
import { AccountCreateEntry } from './TradingAccounts/AccountCreate/AccountCreateEntry';
import { UserPageLayout } from './UserPageLayout';
import { UserUpdateBankDetailsModal } from './modals/UserUpdateBankCardModal';
//@ts-ignore
import PspStore from './psp/pspContext';

export type UserAppProps = { documentDirection: DocumentDirection };

const WELCOME_MODAL_KEY = 'userWelcomeModalShown';

const User = ({ documentDirection }: UserAppProps) => {
  const { i18n } = useTranslation();
  const { accountStore, featuresStore } = useMobxStore();
  const {
    userInfo,
    generalSettings,
    fetchUserProfile,
    userProfile,
    fetchOnboardingConfiguration,
    onboardingConfiguration,
    onboardingConfigurationLoading,
  } = accountStore;

  const { isFeatureEnabled } = featuresStore;

  useDisableNumberInputScroll();

  const [showUserWelcomeModal, setShowUserWelcomeModal] = useState<boolean>(false);
  const [isNavigationMenuOpen, setNavigationMenuOpen] = useState<boolean>(false);
  const themeConfig = getAntdThemeConfig(userGlobalStyles);

  const addEventForNewRegisteredUser = useCallback(() => {
    const newUser = localStorage.getItem(CONSTANTS.NEW_USER);

    if (!newUser) {
      return;
    }

    const { idHash, profile } = userInfo;
    const { clientSourceType } = profile;

    registrationSuccessfulMaPageEvent(idHash, clientSourceType);

    localStorage.removeItem(CONSTANTS.NEW_USER);
  }, [userInfo]);

  useEffect(() => {
    if (!onboardingConfiguration) {
      void fetchOnboardingConfiguration();
    }

    checkUserWelcomeModal();

    addEventForNewRegisteredUser();

    if (Object.keys(userProfile).length === 0) {
      void fetchUserProfile();
    }
  }, [
    addEventForNewRegisteredUser,
    fetchOnboardingConfiguration,
    fetchUserProfile,
    onboardingConfiguration,
    userProfile,
  ]);

  const checkUserWelcomeModal = () => {
    const isUserWelcomeModalShown = localStorage.getItem(WELCOME_MODAL_KEY);

    if (isUserWelcomeModalShown) {
      return;
    }

    setShowUserWelcomeModal(true);
  };

  const closeUserWelcomeModalHandler = () => {
    document.body.style.overflow = '';
    localStorage.setItem(WELCOME_MODAL_KEY, 'true');

    setShowUserWelcomeModal(false);
  };

  const handlerOpenNavMenu = useCallback(() => {
    setNavigationMenuOpen(!isNavigationMenuOpen);
  }, [isNavigationMenuOpen]);

  const checkIfRouteRestricted = useCallback(
    (routeCategory?: ROUTE_CATEGORY, feature?: FeatureTypes) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const isOnboardingRestrictedResult = isOnboardingRestricted(onboardingConfiguration!, userInfo, routeCategory);
      const isFeatureRestrictedResult = !isFeatureEnabled(feature);

      return isFeatureRestrictedResult || isOnboardingRestrictedResult;
    },
    [isFeatureEnabled, onboardingConfiguration, userInfo],
  );

  const renderUserComponent = () => {
    const { brokerParams } = generalSettings;
    const currentLanguage = i18n.language as LanguageType;

    const clientSourceType = userProfile?.profile?.clientSourceType;
    const isDirectClientSourceType = clientSourceType === CLIENT_SOURCE_TYPE.DIRECT;

    const mainLayoutClassName = cn('main-layout');

    if (onboardingConfigurationLoading) {
      return (
        <div className="main-layout">
          <div className="main__loaderContainer">
            <div className="main__content">
              <Preloader />
            </div>
          </div>
        </div>
      );
    }

    const isSocialTradingRouteRestricted = checkIfRouteRestricted(ROUTE_CATEGORY.SOCIAL_TRADING, 'SocialTrading');
    const isTradingContestsRestricted = checkIfRouteRestricted(ROUTE_CATEGORY.TRADING_CONTESTS, 'TradingContests');
    const isLoyaltyProgramRestricted = checkIfRouteRestricted(ROUTE_CATEGORY.LOYALTY_PROGRAMS, 'LoyaltyPrograms');
    const isBonusesRestricted = checkIfRouteRestricted(ROUTE_CATEGORY.BONUSES, 'Bonuses');

    const isCreateLpoaViaLinkRouteRestricted = !generalSettings.lpoaEnabledForMamAccounts;
    const isTraidingToolsRestricted = checkIfRouteRestricted(ROUTE_CATEGORY.TRAIDING_TOOLS);
    const isFAQRestricted = checkIfRouteRestricted(ROUTE_CATEGORY.FAQ);
    const isEducationPortalRestricted = checkIfRouteRestricted(ROUTE_CATEGORY.EDUCATION_PORTAL);
    const isPartnershipRestricted = checkIfRouteRestricted(ROUTE_CATEGORY.PARTNERSHIP);

    const isRouteRestricted = checkIfRouteRestricted();

    return (
      <PspStore>
        <div className={mainLayoutClassName}>
          <UserPageLayout
            user={userInfo}
            handlerOpenNavMenu={handlerOpenNavMenu}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            onboardingConfiguration={onboardingConfiguration!}
            generalSettings={generalSettings}
            documentDirection={documentDirection}
            className="main">
            <div className="main__content--member">
              <Suspense fallback={<Preloader />}>
                <Switch>
                  <RouteWithPageTitle
                    pageTitle="Home"
                    path={EnumUserRoute.member}
                    exact
                    component={UserTradingAccountsLayoutAsync}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="User Tickets"
                    path={EnumUserRoute.ticketPage}
                    component={TicketAsync}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Trading Accounts"
                    path={EnumUserRoute.tradingAccountsPage}
                    component={TradingAccountsRouters}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Trading Contests"
                    path={EnumUserRoute.tradingContestPage}
                    component={TradingContestRouterAsync}
                    routeIsNotAllowed={isTradingContestsRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Loyalty Program"
                    path={EnumUserRoute.loyaltyProgramPage}
                    component={LoyaltyProgramRouterAsync}
                    routeIsNotAllowed={isLoyaltyProgramRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Notifications"
                    path={EnumUserRoute.notificationsPage}
                    component={NotificationAsync}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Settings"
                    path={EnumUserRoute.settingsPage}
                    component={SettingsAsync}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Profile"
                    path={EnumUserRoute.profilePage}
                    render={(props) => <ProfileAsync {...props} />}
                  />
                  <RouteWithPageTitle
                    pageTitle="Psp"
                    path="/member/psp"
                    component={PspAsync}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Partnership"
                    path={EnumUserRoute.partnershipPage}
                    component={PartnershipAsync}
                    routeIsNotAllowed={isPartnershipRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Sales User Info"
                    path={EnumUserRoute.salesUserDetails}
                    exact
                    component={UserDetailsAsync}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Sales User Trading Account"
                    path={EnumUserRoute.salesUserTradingAccountDetails}
                    exact
                    component={TradingAccountDetails}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Sales User Transaction Result"
                    path={EnumUserRoute.salesUserDepositDetails}
                    exact
                    component={PspOperationDetails}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Sales User Transaction Result"
                    path={EnumUserRoute.salesUserWithdrawalDetails}
                    exact
                    component={PspOperationDetails}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Sales"
                    path={EnumUserRoute.sales}
                    render={() => <SalesAsync />}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="FAQ"
                    path="/member/helpcenter/faq"
                    component={UserFAQAsync}
                    routeIsNotAllowed={isFAQRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Education Portal"
                    path={EnumUserRoute.educationPortal}
                    component={EducationPortalAsync}
                    routeIsNotAllowed={isEducationPortalRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Legal Documents"
                    path="/member/helpcenter/documents"
                    render={(props: RouteProps) => <LegalDocumentsAsync user={userInfo} {...props} />}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Bonuses"
                    path="/member/bonuses"
                    component={BonusesAsync}
                    routeIsNotAllowed={isBonusesRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Sign LPOA"
                    path="/member/createlpoa"
                    render={(props) => (
                      <MamCreateLPOAViaLinkAsync user={userInfo} generalSettings={generalSettings} {...props} />
                    )}
                    routeIsNotAllowed={isCreateLpoaViaLinkRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Legal Trading Platforms"
                    path={EnumUserRoute.tradingPlatforms}
                    component={LegalTradingPlatformAsync}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Legal Trading Tools"
                    path={EnumUserRoute.tradingTools}
                    component={LegalTradingToolsAsync}
                    routeIsNotAllowed={isTraidingToolsRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Masters Rating"
                    path={EnumUserRoute.mastersRatingPage}
                    render={(props) => <MastersRatingAsync {...props} />}
                    routeIsNotAllowed={isSocialTradingRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="My Statistics"
                    path={EnumUserRoute.myStatisticsPage}
                    exact
                    component={MyStatisticsAsync}
                    routeIsNotAllowed={isSocialTradingRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Statistics Details - Investors"
                    path={EnumUserRoute.myInvestorsPage}
                    component={InvestorRouterAsync}
                    routeIsNotAllowed={isSocialTradingRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Statistics Details - Masters"
                    path={EnumUserRoute.myMastersPage}
                    render={(props) => <MastersDetailsAsync {...props} />}
                    routeIsNotAllowed={isSocialTradingRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Operation History"
                    path={EnumUserRoute.operationHistoryPage}
                    component={OperationHistoryAsync}
                    routeIsNotAllowed={isSocialTradingRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Master Profile"
                    path={EnumUserRoute.masterProfilePage}
                    render={(props) => <MasterProfileAsync {...props} />}
                    routeIsNotAllowed={isSocialTradingRouteRestricted}
                  />
                  <RouteWithPageTitle
                    pageTitle="Referral Link"
                    path={EnumUserRoute.referralLinkPage}
                    component={ReferralLinkDetailsAsync}
                    routeIsNotAllowed={isRouteRestricted}
                  />
                  <RouteWithPageTitle pageTitle="Page not found" render={() => <Page404 redirectTo="/member" />} />
                </Switch>
              </Suspense>
            </div>
            {isDirectClientSourceType && showUserWelcomeModal && (
              <UserWelcomeModal onCloseHandler={closeUserWelcomeModalHandler} brokerParams={brokerParams} />
            )}
            <UserWithStatusNeedToUpdateModal
              user={userInfo}
              brokerParams={brokerParams}
              currentLanguage={currentLanguage}
            />
          </UserPageLayout>

          <HubSpotLiveChat />
          <UserJurisdictionPopupModalWithRouter />
          <UserUpdateBankDetailsModal />
          <AccountCreateEntry />
        </div>
      </PspStore>
    );
  };

  return (
    <TelemetryProvider>
      <ConfigProvider theme={themeConfig} direction={documentDirection}>
        <GuardCountrySupport>{renderUserComponent()}</GuardCountrySupport>
      </ConfigProvider>
    </TelemetryProvider>
  );
};

export default connectToContext(observer(User));
